import React, { useState } from 'react';
import { useStore } from '../../contexts/StoreContext';
import { Field } from '../ui/Field';
import { ConsentChecks } from '../ConsentChecks';
import { LeadSuccess } from '../LeadSuccess';
import { tradeInYears } from '../../data/options';
import { Lead } from '../../types/lead';
import { vehicleLabel } from '../../utils/catalog';
import { cleanText, isValidEmail, isValidName, isValidPhone, normalizePhone } from '../../utils/validation';
type Errors = Partial<Record<'brand' | 'model' | 'year' | 'km' | 'name' | 'phone' | 'email' | 'consent', string>>;
export function TradeInForm() {
  const {
    addLead,
    vehicles,
    brands
  } = useStore();
  const [f, setF] = useState({
    brand: '',
    model: '',
    year: 0,
    km: '',
    target: '',
    name: '',
    phone: '',
    email: ''
  });
  const [consent, setConsent] = useState(false);
  const [contact, setContact] = useState(true);
  const [errors, setErrors] = useState<Errors>({});
  const [lead, setLead] = useState<Lead | null>(null);
  const set = <K extends keyof typeof f,>(k: K, v: (typeof f)[K]) => setF(x => ({
    ...x,
    [k]: v
  }));
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const er: Errors = {};
    const km = Number(f.km.replace(/\D/g, ''));
    if (f.brand.trim().length < 2) er.brand = 'Ingresá la marca de tu usado.';
    if (f.model.trim().length < 1) er.model = 'Ingresá el modelo.';
    if (!f.year) er.year = 'Elegí el año.';
    if (!f.km || km > 1000000) er.km = 'Ingresá el kilometraje.';
    if (!isValidName(f.name)) er.name = 'Ingresá tu nombre y apellido.';
    if (!isValidPhone(f.phone)) er.phone = 'Ingresá un teléfono con código de área.';
    if (f.email && !isValidEmail(f.email)) er.email = 'Ingresá un email válido.';
    if (!consent) er.consent = 'Necesitamos tu consentimiento para continuar.';
    setErrors(er);
    if (Object.keys(er).length) return;
    const target = vehicles.find(v => v.id === f.target);
    setLead(addLead({
      channel: 'usado',
      name: cleanText(f.name, 80),
      phone: normalizePhone(f.phone),
      email: cleanText(f.email, 120),
      contactPref: 'whatsapp',
      vehicleId: target?.id ?? null,
      vehicleLabel: target ? vehicleLabel(target, brands) : null,
      price: target?.price ?? null,
      down: null,
      term: null,
      simulation: null,
      message: `Entrega usado: ${cleanText(f.brand, 40)} ${cleanText(f.model, 60)} ${f.year}, ${km.toLocaleString('es-AR')} km.`,
      credit: null,
      tradeIn: {
        brand: cleanText(f.brand, 40),
        model: cleanText(f.model, 60),
        year: f.year,
        km
      },
      consents: {
        dataProcessing: consent,
        contact,
        at: new Date().toISOString()
      }
    }));
  };
  if (lead) {
    return <LeadSuccess lead={lead} data-id="element-831" data-mp="3g4rj1u3q5s8d">Un asesor te contactará para coordinar la tasación de tu usado.</LeadSuccess>;
  }
  return <form onSubmit={onSubmit} noValidate className="space-y-4" data-id="element-832" data-mp="2ioc0741ejwv6">
      <h3 className="text-sm font-semibold" data-id="element-833" data-mp="2nmn8hac1tzba">Tu usado</h3>
      <div className="grid gap-4 sm:grid-cols-2" data-id="element-834" data-mp="2g45uorx53yci">
        <Field label="Marca" htmlFor="ti-brand" error={errors.brand} data-id="element-835" data-mp="2g2usuxn990o1">
          <input id="ti-brand" className="input" value={f.brand} onChange={e => set('brand', e.target.value)} maxLength={40} data-id="element-836" data-mp="2b4jjsp4sau6v" />
        </Field>
        <Field label="Modelo y versión" htmlFor="ti-model" error={errors.model} data-id="element-837" data-mp="26457uw02b03k">
          <input id="ti-model" className="input" value={f.model} onChange={e => set('model', e.target.value)} maxLength={60} data-id="element-838" data-mp="2163oe9yq4yrk" />
        </Field>
        <Field label="Año" htmlFor="ti-year" error={errors.year} data-id="element-839" data-mp="1x74l0tsn5a28">
          <select id="ti-year" className="input" value={f.year} onChange={e => set('year', Number(e.target.value))} data-id="element-840" data-mp="1sd0icmqero6y">
            <option value={0} data-id="element-841" data-mp="1nde4qc6o45xm">Seleccioná</option>
            {tradeInYears.map(y => <option key={y} value={y} data-id="element-842" data-mp="1ihyqhj4r080o">{y}</option>)}
          </select>
        </Field>
        <Field label="Kilometraje" htmlFor="ti-km" error={errors.km} data-id="element-843" data-mp="1dg9h07x6c2ep">
          <input id="ti-km" className="input" inputMode="numeric" value={f.km} onChange={e => set('km', e.target.value.replace(/\D/g, '').slice(0, 7))} placeholder="Ej.: 45000" data-id="element-844" data-mp="19f7cbbkhmy5c" />
        </Field>
      </div>
      <Field label="0 km que te interesa (opcional)" htmlFor="ti-target" data-id="element-845" data-mp="235zrgw5jh3qv">
        <select id="ti-target" className="input" value={f.target} onChange={e => set('target', e.target.value)} data-id="element-846" data-mp="1pe1kaqve1hxc">
          <option value="" data-id="element-847" data-mp="1tf50xfl9fhzm">Todavía no lo decidí</option>
          {vehicles.filter(v => v.published).map(v => <option key={v.id} value={v.id} data-id="element-848" data-mp="1ffcdpu8xzjwe">{vehicleLabel(v, brands)}</option>)}
        </select>
      </Field>
      <h3 className="pt-2 text-sm font-semibold" data-id="element-849" data-mp="1kf8hpthn3bby">Tus datos</h3>
      <Field label="Nombre y apellido" htmlFor="ti-name" error={errors.name} data-id="element-850" data-mp="1kcm359wbzvk8">
        <input id="ti-name" className="input" value={f.name} onChange={e => set('name', e.target.value)} autoComplete="name" maxLength={80} data-id="element-851" data-mp="16h0i1dt7xvx3" />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2" data-id="element-852" data-mp="30syppbbvqjqv">
        <Field label="Teléfono" htmlFor="ti-phone" error={errors.phone} data-id="element-853" data-mp="30r45jz8fmvnu">
          <input id="ti-phone" className="input" inputMode="tel" value={f.phone} onChange={e => set('phone', e.target.value)} autoComplete="tel" maxLength={20} data-id="element-854" data-mp="34ua5yq8v5sme" />
        </Field>
        <Field label="Email (opcional)" htmlFor="ti-email" error={errors.email} data-id="element-855" data-mp="p6wja7c66hid">
          <input id="ti-email" type="email" className="input" value={f.email} onChange={e => set('email', e.target.value)} autoComplete="email" maxLength={120} data-id="element-856" data-mp="12xtb3vy9uuod" />
        </Field>
      </div>
      <ConsentChecks consent={consent} contact={contact} onConsent={setConsent} onContact={setContact} error={errors.consent} purpose="cotizar mi usado como parte de pago" data-id="element-857" data-mp="1cv7dwsr9vky7" />
      <button type="submit" className="btn btn-primary h-12 w-full" data-id="element-858" data-mp="17ypapxblbunz">Enviar consulta</button>
      <p className="text-[11px] text-mist" data-id="element-859" data-mp="1lu1fkxjgn1im">La toma del usado está sujeta a peritaje y tasación presencial.</p>
    </form>;
}